import {
    DoNotDisturbOutlined,
    DraftsOutlined,
    PaidOutlined,
    PriorityHighOutlined,
    ReceiptLongOutlined,
    ScheduleOutlined,
} from '@mui/icons-material';
import {FC, ReactNode} from 'react';
import {useIntl} from 'react-intl';
import {StatsStrip} from 'src/components/common/StatsStrip';
import {StatsStripTone} from 'src/components/common/StatsStrip/StatsStrip.types';
import {InvoiceStatusEnum} from 'src/enums';
import {InvoiceStatsStripType} from './InvoiceStatsStrip.types';

/** An invoice's life, in the order it goes through it, with the two dead ends at the end. */
const STRIP_STATUSES: InvoiceStatusEnum[] = [
    InvoiceStatusEnum.PENDING,
    InvoiceStatusEnum.PARTIAL,
    InvoiceStatusEnum.UNPAID,
    InvoiceStatusEnum.PAID,
    InvoiceStatusEnum.DRAFT,
    InvoiceStatusEnum.CANCELED,
];

const STATUS_ICONS: Record<string, ReactNode> = {
    [InvoiceStatusEnum.PENDING]: <ScheduleOutlined/>,
    [InvoiceStatusEnum.PARTIAL]: <PriorityHighOutlined/>,
    [InvoiceStatusEnum.UNPAID]: <PriorityHighOutlined/>,
    [InvoiceStatusEnum.PAID]: <PaidOutlined/>,
    [InvoiceStatusEnum.DRAFT]: <DraftsOutlined/>,
    [InvoiceStatusEnum.CANCELED]: <DoNotDisturbOutlined/>,
};

/**
 * The tones ColoredStatus already gives these four, so the strip and the table's own status column
 * cannot disagree. Draft and canceled have no case there and fall to grey, which is right: neither
 * is money owed.
 */
const STATUS_TONES: Record<string, StatsStripTone> = {
    [InvoiceStatusEnum.PENDING]: 'primary',
    [InvoiceStatusEnum.PARTIAL]: 'warning',
    [InvoiceStatusEnum.UNPAID]: 'brand',
    [InvoiceStatusEnum.PAID]: 'success',
    [InvoiceStatusEnum.DRAFT]: 'neutral',
    [InvoiceStatusEnum.CANCELED]: 'neutral',
};

export const InvoiceStatsStrip: FC<InvoiceStatsStripType> = ({
                                                                 data,
                                                                 activeStatus,
                                                                 onSelectStatus,
                                                             }) => {
    const intl = useIntl();

    return (
        <StatsStrip
            icon={<ReceiptLongOutlined/>}
            title="invoices"
            subtitle={intl.formatMessage(
                {id: 'invoiceStatsSubtitle'},
                {invoices: data?.totalCount ?? 0}
            )}
            tiles={STRIP_STATUSES.map((status) => ({
                key: status,
                label: status,
                // A status with no invoices at all is missing from the map rather than zero, so the
                // zero is supplied here — a dash would read as "not counted yet".
                value: data?.byStatus?.[status]?.count ?? (data ? 0 : undefined),
                icon: STATUS_ICONS[status],
                tone: STATUS_TONES[status],
                active: activeStatus === status,
                onClick: onSelectStatus ? () => onSelectStatus(status) : undefined,
            }))}
        />
    );
};
